import type { QueryClient } from "@tanstack/react-query";

import type { HubRecordsDto } from "@bafe/api-client";

import { queryKeys } from "../../lib/query.js";
import type { TaskBoardStatus } from "./task-board-order.js";

export type BoardMovePatch = {
	taskId: string;
	status: TaskBoardStatus;
	boardPosition: number;
};

type PersistDeps = {
	patchTask: (taskId: string, input: { status: TaskBoardStatus; boardPosition: number }) => Promise<void>;
	client: QueryClient;
	onError: (message: string) => void;
	queryKey?: readonly unknown[];
};

const FLUSH_DEBOUNCE_MS = 180;
const SYNC_DELAY_MS = 550;
const RETRY_DELAY_MS = 320;

/** Serialises board PATCHes and coalesces hub refetches (avoids refetch storms during fast drags). */
export function createBoardMovePersister(deps: PersistDeps) {
	const queryKey = deps.queryKey ?? queryKeys.hubRecords;
	let chain = Promise.resolve();
	let syncTimer: ReturnType<typeof setTimeout> | null = null;
	let flushTimer: ReturnType<typeof setTimeout> | null = null;
	const pendingByTask = new Map<string, BoardMovePatch>();

	const scheduleSync = () => {
		if (syncTimer) clearTimeout(syncTimer);
		syncTimer = setTimeout(() => {
			syncTimer = null;
			void deps.client.invalidateQueries({ queryKey });
		}, SYNC_DELAY_MS);
	};

	const patchMove = async (move: BoardMovePatch): Promise<boolean> => {
		if (!Number.isFinite(move.boardPosition)) return true;
		try {
			await deps.patchTask(move.taskId, {
				status: move.status,
				boardPosition: move.boardPosition,
			});
			return true;
		} catch {
			return false;
		}
	};

	const flushBatch = async (): Promise<void> => {
		for (let attempt = 0; attempt < 2 && pendingByTask.size > 0; attempt += 1) {
			if (attempt > 0 && pendingByTask.size > 0) {
				await new Promise((resolve) => setTimeout(resolve, RETRY_DELAY_MS));
			}
			const batch = [...pendingByTask.values()];
			pendingByTask.clear();
			const retry: BoardMovePatch[] = [];
			for (const move of batch) {
				const ok = await patchMove(move);
				if (ok) continue;
				if (pendingByTask.has(move.taskId)) {
					continue;
				}
				retry.push(move);
			}
			for (const move of retry) {
				pendingByTask.set(move.taskId, move);
			}
		}

		if (pendingByTask.size > 0) {
			pendingByTask.clear();
			await deps.client.invalidateQueries({ queryKey });
			deps.onError("Could not save the new task order. The board was refreshed.");
			return;
		}

		scheduleSync();
	};

	const scheduleFlush = () => {
		if (flushTimer) clearTimeout(flushTimer);
		flushTimer = setTimeout(() => {
			flushTimer = null;
			chain = chain.then(flushBatch).catch(() => {});
		}, FLUSH_DEBOUNCE_MS);
	};

	return {
		enqueue(move: BoardMovePatch) {
			if (!Number.isFinite(move.boardPosition)) return chain;
			pendingByTask.set(move.taskId, move);
			scheduleFlush();
			return chain;
		},
	};
}

export function applyBoardMoveOptimistic(
	client: QueryClient,
	taskId: string,
	status: TaskBoardStatus,
	boardPosition: number,
	queryKey: readonly unknown[] = queryKeys.hubRecords,
) {
	client.setQueryData<HubRecordsDto>(queryKey, (current) => {
		if (!current) return current;
		return {
			...current,
			tasks: current.tasks.map((row) => (
				row.id === taskId ? { ...row, status, boardPosition } : row
			)),
		};
	});
}
